import * as React from "react";

import { H3 } from "@impero-com/common/components/html";
import * as Cmp from "@impero-com/common/utils/comparison";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import memoizeOne from "memoize-one";

import { EntityView } from "api/generated/model";
import * as R from "org/risks/types";
import { Action, AssignEntities } from "org/risks/widgets/assignEntities";

import * as T from "../types";

import {
    DescendantsEntitiesTable,
    DescendantsTableEntity,
} from "./DescendantsEntitiesTable";
import * as theme from "./theme.css";

class NodeEntitiesTab extends React.PureComponent<NodeEntitiesTabProps> {
    descendantsEntitiesDataSource = memoizeOne(
        (
            descendantsEntities: Record<number, string[]>,
            entities: Record<number, EntityView>
        ) =>
            this.buildDescendantsEntitiesDataSource(
                descendantsEntities,
                entities
            )
    );

    buildDescendantsEntitiesDataSource = (
        descendantsEntities: Record<number, string[]>,
        entities: Record<number, EntityView>
    ): Array<DescendantsTableEntity> => {
        const data = Object.entries(descendantsEntities).map(
            ([entityId, paths]) => {
                const dte: DescendantsTableEntity = {
                    entity: entities[Number(entityId)],
                    paths,
                };
                return dte;
            }
        );
        data.sort(Cmp.map((te) => te.entity.name, Cmp.string()));
        return data;
    };

    render = () => {
        const {
            ancestorsEntities,
            nodeEntities,
            entities,
            descendantsEntities,
            canDeleteEntityLink,
            node,
            riskDirectoryView,
        } = this.props;

        const descendantsDataSource = this.descendantsEntitiesDataSource(
            descendantsEntities,
            entities
        );
        const { edit } = riskDirectoryView.content.actions;

        let assignEntities: React.ReactNode;
        if (edit) {
            assignEntities = (
                <AssignEntities
                    type="EDIT"
                    id="assignEntities"
                    value={nodeEntities}
                    onAction={(action, entityId) => {
                        this.props.onQueueAsyncNodeUpdate(
                            Promise.resolve(
                                entityUpdateAction(action, entityId)
                            )
                        );
                    }}
                    onChange={() => {
                        /* action based changes handled by onAction */
                    }}
                    confirmUnassign={{
                        node,
                        riskDirectoryView,
                    }}
                    ancestorsEntities={ancestorsEntities}
                    canDeleteEntityLink={canDeleteEntityLink}
                    entities={entities}
                    descendantsEntities={descendantsEntities}
                />
            );
        } else {
            assignEntities = (
                <AssignEntities
                    type="READ_ONLY"
                    ancestorsEntities={ancestorsEntities}
                    entities={entities}
                    nodeEntities={nodeEntities}
                />
            );
        }
        let childrenEntities: React.ReactNode;
        if (node.type !== "Risk") {
            childrenEntities = (
                <>
                    <div className={theme.headingChildren}>
                        {t(
                            "RiskDirectory_NodeUpdate_Entities_AssignedToChildren"
                        )}
                    </div>
                    <DescendantsEntitiesTable
                        entities={descendantsDataSource}
                    />
                </>
            );
        } else {
            childrenEntities = null;
        }

        return (
            <div>
                <H3>{t("RiskMapResponses_RiskMapEntities")}</H3>
                {assignEntities}
                {childrenEntities}
            </div>
        );
    };
}

interface NodeEntitiesTabProps {
    riskDirectoryView: R.RiskDirectoryView;
    node: R.RiskDirectoryNode | R.StandardNode;
    ancestorsEntities: Record<number, string>;
    nodeEntities: number[];
    descendantsEntities: Record<number, string[]>;
    entities: Record<number, EntityView>;
    canDeleteEntityLink: boolean;
    onQueueAsyncNodeUpdate: (
        promiseAction: Promise<T.NodeUpdateAction<T.DirectoryNode>>
    ) => Promise<void>;
}

function entityUpdateAction(
    func: Action,
    entityId: number
): T.NodeUpdateAction<T.DirectoryNode> {
    return (node) => {
        let oldEntityIds: Array<number>;
        switch (node.type) {
            case "RiskDirectory": {
                oldEntityIds = node.content.scope.entityIds;
                const entityIds = func(oldEntityIds, entityId);
                if (entityIds !== oldEntityIds) {
                    return {
                        scope: { type: "UpdatedGlobal", entityIds },
                    };
                } else {
                    return undefined;
                }
            }
            case "Process": {
                if (node.content.scope.type !== "Global") {
                    throw new Error("Not implemented");
                }
                oldEntityIds = node.content.scope.entityIds;
                const entityIds = func(oldEntityIds, entityId);
                if (entityIds !== oldEntityIds) {
                    return {
                        scope: { type: "UpdatedGlobal", entityIds },
                    };
                } else {
                    return undefined;
                }
            }
            case "Risk": {
                if (node.content.scope.type !== "GlobalWithContent") {
                    throw new Error("Not implemented");
                }
                oldEntityIds = node.content.scope.entityIds;
                const entityIds = func(oldEntityIds, entityId);
                if (entityIds !== oldEntityIds) {
                    return {
                        scope: { type: "UpdatedGlobalWithContent", entityIds },
                    };
                } else {
                    return undefined;
                }
            }
            default:
                return assertNever(node);
        }
    };
}

export { NodeEntitiesTab, type NodeEntitiesTabProps };
